import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Upload, CheckCircle, AlertCircle, Loader2, Database, Download, Save, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminImportPage() {
    const { isAdmin, token } = useAuth();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportResult, setExportResult] = useState(null);
    const [popinaStart, setPopinaStart] = useState('2026-05-01');
    const [popinaSyncing, setPopinaSyncing] = useState(false);
    const [popinaResult, setPopinaResult] = useState(null);

    // Rediriger si pas admin
    if (!isAdmin) {
        return <Navigate to="/dashboard" replace />;
    }

    const handlePopinaSync = async () => {
        setPopinaSyncing(true);
        setPopinaResult(null);
        try {
            const response = await fetch(`${API_URL}/api/admin/sync-popina?start_date=${popinaStart}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (response.ok) {
                setPopinaResult({ success: true, message: data.message });
                toast.success(data.message);
            } else {
                setPopinaResult({ success: false, message: data.detail || 'Erreur' });
                toast.error(data.detail || 'Erreur lors du lancement');
            }
        } catch (error) {
            setPopinaResult({ success: false, message: error.message });
            toast.error('Erreur de connexion au serveur');
        } finally {
            setPopinaSyncing(false);
        }
    };

    // Fonction pour générer et télécharger l'export JSON
    const handleExportJSON = async () => {
        setExporting(true);
        setExportResult(null);
        
        try {
            // Étape 1: Générer le fichier
            toast.info('Génération de l\'export en cours...');
            const genResponse = await fetch(`${API_URL}/api/admin/export-db-dump`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Vérifier si la réponse est du JSON valide
            const contentType = genResponse.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('Erreur serveur - la génération a échoué. Veuillez réessayer.');
            }

            const genData = await genResponse.json();

            if (!genResponse.ok) {
                throw new Error(genData.detail || 'Erreur lors de la génération');
            }

            // Étape 2: Télécharger le fichier
            toast.info('Téléchargement en cours...');
            const downloadResponse = await fetch(`${API_URL}/api/admin/download-db-dump`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!downloadResponse.ok) {
                const errorText = await downloadResponse.text();
                if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
                    throw new Error('Le fichier est trop volumineux pour le proxy. Essayez depuis la preview.');
                }
                throw new Error('Erreur lors du téléchargement');
            }

            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'export_production.json.gz';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            setExportResult({
                success: true,
                message: `Export terminé (${genData.compressed_size_mb || genData.file_size_mb} MB)`,
                details: genData.collections
            });
            toast.success('Export téléchargé avec succès !');
        } catch (error) {
            console.error('Export error:', error);
            setExportResult({
                success: false,
                message: error.message || 'Erreur lors de l\'export'
            });
            toast.error(error.message || 'Erreur lors de l\'export');
        } finally {
            setExporting(false);
        }
    };

    // Fonction pour exporter uniquement les données essentielles (beaucoup plus petit)
    const handleExportEssential = async () => {
        setExporting(true);
        setExportResult(null);
        
        try {
            toast.info('Génération de l\'export essentiel...');
            const genResponse = await fetch(`${API_URL}/api/admin/export-essential`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const contentType = genResponse.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('Erreur serveur');
            }

            const genData = await genResponse.json();

            if (!genResponse.ok) {
                throw new Error(genData.detail || 'Erreur lors de la génération');
            }

            // Télécharger
            const downloadResponse = await fetch(`${API_URL}/api/admin/download-essential`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!downloadResponse.ok) {
                throw new Error('Erreur lors du téléchargement');
            }

            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'export_essential.json.gz';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();

            setExportResult({
                success: true,
                message: `Export essentiel: ${genData.size_mb} MB`,
                details: genData.collections
            });
            toast.success('Export téléchargé !');
        } catch (error) {
            console.error('Export error:', error);
            setExportResult({ success: false, message: error.message });
            toast.error(error.message);
        } finally {
            setExporting(false);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setResult(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            toast.error('Veuillez sélectionner un fichier');
            return;
        }

        setUploading(true);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            toast.info('Import en cours... Cela peut prendre quelques secondes.');
            
            const response = await fetch(`${API_URL}/api/admin/import-db-dump`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            // Vérifier si la réponse est du JSON valide
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                const errorText = await response.text();
                if (errorText.includes('<!DOCTYPE') || errorText.includes('<html')) {
                    throw new Error('Le fichier est trop volumineux (max ~10MB). Essayez de compresser le fichier ou contactez le support.');
                }
                throw new Error('Erreur serveur inattendue');
            }

            const data = await response.json();

            if (response.ok) {
                setResult({
                    success: true,
                    message: data.message || 'Import réussi !',
                    details: data.details
                });
                toast.success('Import réussi !');
                setFile(null);
                // Reset file input
                const fileInput = document.getElementById('json-import-input');
                if (fileInput) fileInput.value = '';
            } else {
                setResult({
                    success: false,
                    message: data.detail || 'Erreur lors de l\'import'
                });
                toast.error(data.detail || 'Erreur lors de l\'import');
            }
        } catch (error) {
            console.error('Upload error:', error);
            setResult({
                success: false,
                message: error.message || 'Erreur de connexion au serveur. Le fichier est peut-être trop volumineux.'
            });
            toast.error('Erreur de connexion au serveur');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8">
            <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-500/20 rounded-2xl mb-4">
                        <Database className="w-8 h-8 text-emerald-400" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">
                        Administration
                    </h1>
                    <p className="text-slate-400">
                        Migrez les données entre Preview et Production
                    </p>
                </div>

                {/* Resync Popina */}
                <Card className="bg-gradient-to-r from-sky-900/50 to-indigo-900/50 border-sky-500/30 backdrop-blur mb-6" data-testid="popina-sync-card">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <RefreshCw className="w-5 h-5 text-sky-400" />
                            Resynchronisation Popina (Sous les pins)
                        </CardTitle>
                        <CardDescription className="text-sky-200/80">
                            Recalcule les ventes journalières depuis les rapports de clôture Popina, à partir de la date choisie
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="flex gap-3">
                            <input
                                type="date"
                                value={popinaStart}
                                onChange={(e) => setPopinaStart(e.target.value)}
                                className="h-11 px-3 rounded-md bg-slate-800 border border-slate-600 text-white text-sm"
                                data-testid="popina-sync-start-date"
                            />
                            <Button
                                onClick={handlePopinaSync}
                                disabled={popinaSyncing || !popinaStart}
                                className="flex-1 h-11 bg-sky-600 hover:bg-sky-700"
                                data-testid="popina-sync-button"
                            >
                                {popinaSyncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                                Lancer la resynchronisation
                            </Button>
                        </div>
                        {popinaResult && (
                            <p className={`text-sm ${popinaResult.success ? 'text-green-400' : 'text-red-400'}`} data-testid="popina-sync-result">
                                {popinaResult.message}{popinaResult.success && ' — traitement en arrière-plan, les données se mettent à jour progressivement.'}
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* Export/Import JSON */}
                <Card className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border-emerald-500/30 backdrop-blur mb-6">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Save className="w-5 h-5 text-emerald-400" />
                            Migration des données
                        </CardTitle>
                        <CardDescription className="text-emerald-200/80">
                            Exportez les données de la preview et importez-les sur la production
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Export Options */}
                        <div className="space-y-3">
                            <p className="text-xs text-slate-400 font-medium">ÉTAPE 1: Exporter (depuis la preview)</p>
                            <div className="grid grid-cols-2 gap-3">
                                <Button
                                    onClick={handleExportEssential}
                                    disabled={exporting}
                                    className="h-14 bg-emerald-600 hover:bg-emerald-700 flex-col"
                                    data-testid="export-essential-button"
                                >
                                    {exporting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mb-1 animate-spin" />
                                            <span className="text-xs">Export...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4 mb-1" />
                                            <span className="text-xs">Export Essentiel</span>
                                            <span className="text-[10px] opacity-70">~70 KB (recommandé)</span>
                                        </>
                                    )}
                                </Button>
                                <Button
                                    onClick={handleExportJSON}
                                    disabled={exporting}
                                    variant="outline"
                                    className="h-14 border-emerald-600 text-emerald-400 hover:bg-emerald-900/30 flex-col"
                                    data-testid="export-json-button"
                                >
                                    <Download className="w-4 h-4 mb-1" />
                                    <span className="text-xs">Export Complet</span>
                                    <span className="text-[10px] opacity-70">~6 MB (avec cache)</span>
                                </Button>
                            </div>
                        </div>

                        {/* Import */}
                        <div className="space-y-3">
                            <p className="text-xs text-slate-400 font-medium">ÉTAPE 2: Importer (sur la production)</p>
                            <Button
                                onClick={() => document.getElementById('json-import-input').click()}
                                className="w-full h-14 bg-teal-600 hover:bg-teal-700 flex-col"
                                data-testid="import-json-button"
                            >
                                <Upload className="w-4 h-4 mb-1" />
                                <span className="text-xs">Importer le fichier JSON</span>
                            </Button>
                        </div>
                        
                        <input
                            type="file"
                            id="json-import-input"
                            accept=".json,.gz"
                            onChange={handleFileChange}
                            className="hidden"
                        />
                        
                        {/* Fichier sélectionné */}
                        {file && (
                            <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                                <p className="text-white text-sm mb-3">
                                    📁 Fichier sélectionné: <strong>{file.name}</strong>
                                </p>
                                <Button
                                    onClick={handleUpload}
                                    disabled={uploading}
                                    className="w-full bg-teal-600 hover:bg-teal-700"
                                >
                                    {uploading ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Import en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            Lancer l'import
                                        </>
                                    )}
                                </Button>
                            </div>
                        )}

                        {/* Résultat de l'export */}
                        {exportResult && (
                            <div className={`rounded-xl p-4 ${
                                exportResult.success 
                                    ? 'bg-green-500/10 border border-green-500/20' 
                                    : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                                <div className="flex items-start gap-3">
                                    {exportResult.success ? (
                                        <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                                    )}
                                    <div>
                                        <p className={`font-medium ${exportResult.success ? 'text-green-400' : 'text-red-400'}`}>
                                            {exportResult.message}
                                        </p>
                                        {exportResult.details && (
                                            <div className="mt-2 text-xs text-slate-400 grid grid-cols-2 gap-1">
                                                {Object.entries(exportResult.details).slice(0, 6).map(([key, val]) => (
                                                    <span key={key}>{key}: {val}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Résultat de l'import */}
                        {result && (
                            <div className={`rounded-xl p-4 ${
                                result.success 
                                    ? 'bg-green-500/10 border border-green-500/20' 
                                    : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                                <div className="flex items-start gap-3">
                                    {result.success ? (
                                        <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                                    ) : (
                                        <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                                    )}
                                    <div>
                                        <p className={`font-medium ${result.success ? 'text-green-400' : 'text-red-400'}`}>
                                            {result.message}
                                        </p>
                                        {result.details?.collections_imported && (
                                            <div className="mt-2 text-xs text-slate-400">
                                                {result.details.collections_imported.map((c, i) => (
                                                    <span key={i} className="mr-2">{c.name}: {c.count}</span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Instructions */}
                        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                            <h4 className="text-emerald-400 font-medium mb-2">📋 Procédure après redéploiement</h4>
                            <ol className="text-slate-300 text-sm space-y-2">
                                <li><span className="text-emerald-400">1.</span> Sur la <strong>preview</strong>, cliquez sur "Exporter JSON"</li>
                                <li><span className="text-emerald-400">2.</span> Le fichier (~50 MB) sera téléchargé automatiquement</li>
                                <li><span className="text-emerald-400">3.</span> Sur la <strong>production</strong>, cliquez sur "Importer JSON"</li>
                                <li><span className="text-emerald-400">4.</span> Sélectionnez le fichier téléchargé et validez</li>
                            </ol>
                            <p className="text-slate-500 text-xs mt-3">
                                L'export contient: budgets, ventes (cache Trivec), réservations, avis, restaurants, utilisateurs...
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
